// eslint-disable-next-line max-classes-per-file
import React, { useEffect, useRef } from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Input, MaskedTextBox } from '@progress/kendo-react-inputs';
import moment from 'moment';
// @ts-ignore
import { DatePicker } from '@progress/kendo-react-dateinputs';
import styles from './grid.module.scss';

export function dropdownFilterCellCanDisable(data, defaultItem, ariaLabel, readOnly) {
  return function (props) {
    const dropDownRef = useRef();

    useEffect(() => {
      const dropdownElement = dropDownRef.current?.element;
      if (!dropdownElement) {
        return;
      }

      // Accessibility scanners target the root combobox span, so label it directly.
      if (dropdownElement.getAttribute('role') === 'combobox') {
        dropdownElement.setAttribute('aria-label', ariaLabel);
        dropdownElement.setAttribute('title', ariaLabel);
      }

      const inputs = dropdownElement.querySelectorAll('.k-input');
      inputs.forEach((item) => {
        item.setAttribute('role', 'option');
      });
    }, []);

    const hasValue = (value) => Boolean(value && value !== defaultItem);

    const onChange = (event) => {
      const _hasValue = hasValue(event.target.value);
      props.onChange({
        value: _hasValue ? event.target.value : '',
        operator: _hasValue ? 'eq' : '',
        syntheticEvent: event.syntheticEvent,
      });
    };

    return (
      <td>
        <div className={`k-filtercell ${styles.filterCellsFilterContainer}`}>
          <DropDownList
            ref={dropDownRef}
            data={data}
            textField="text"
            dataItemKey="id"
            className={styles.filterCellsDropdown}
            onChange={onChange}
            value={props.value || defaultItem}
            defaultItem={defaultItem}
            disabled={readOnly}
            popupSettings={{ className: styles.dropdownPopup }}
          />
        </div>
      </td>
    );
  };
}

export function dropdownFilterCell(data, defaultItem, ariaLabel) {
  return dropdownFilterCellCanDisable(data, defaultItem, ariaLabel, false);
}

export function inputFilterCell(ariaLabel, type = 'text') {
  return class extends React.Component {
    hasValue = (value) => Boolean(value);

    onChange = (event) => {
      const hasValue = this.hasValue(event.target.value);
      this.props.onChange({
        value: hasValue ? event.target.value : '',
        operator: hasValue ? 'eq' : '',
        syntheticEvent: event.syntheticEvent,
      });
    };

    onClearButtonClick = (event) => {
      event.preventDefault();
      this.props.onChange({
        value: '',
        operator: '',
        syntheticEvent: event,
      });
    };

    render() {
      return (
        <td>
          <div className={`k-filtercell ${styles.filterCellsInputContainer}`}>
            <Input onChange={this.onChange} value={this.props.value || ''} aria-label={ariaLabel} type={type} />
          </div>
        </td>
      );
    }
  };
}

export function InputFieldDatePickerMaskedBox(props) {
  const inputRef = useRef(null);
  const [textBoxValue, setTextBoxValue] = React.useState('');

  const onTextboxChange = (event) => {
    if (!/^[0-9]$/.test(event.nativeEvent.data) && event.nativeEvent.data !== null) {
      return;
    }
    setTextBoxValue(event.target.value);
    const momentObj = moment(event.target.value);

    if (momentObj.isValid() || event.target.value === '__/__/____') {
      props.onChange({
        value: momentObj.isValid() ? new Date(momentObj.format('MM/DD/yyyy')) : null,
        operator: 'eq',
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  useEffect(() => {
    if (props.value) {
      setTextBoxValue(moment(props.value).format('MM/DD/yyyy'));
    }
    if (inputRef.current) {
      inputRef.current.element.setAttribute('inputmode', 'numeric');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  return (
    <MaskedTextBox
      adaptive
      mask={'00/00/0000'}
      onChange={(event) => onTextboxChange(event)}
      value={textBoxValue}
      ref={inputRef}
      validityStyles={false}
      style={{ width: '100%' }}
    />
  );
}

export function FilterDatePickerMaskedBox(props) {
  const inputRef = useRef(null);
  const [textBoxValue, setTextBoxValue] = React.useState('');
  const dateInputLabel = props['aria-label'] || props.ariaLabel || 'Date Filter Input';

  const onTextboxChange = (event) => {
    if (!/^[0-9]$/.test(event.nativeEvent.data) && event.nativeEvent.data !== null) {
      return;
    }
    setTextBoxValue(event.target.value);
    const momentObj = moment(event.target.value);
    // used by a filter component do not allowed dates before 01/01/1900
    const IsAcceptableDate = momentObj >= moment('01/01/1900');

    if ((momentObj.isValid() && IsAcceptableDate) || event.target.value === '__/__/____') {
      props.onChange({
        value: momentObj.isValid() ? new Date(momentObj.format('MM/DD/yyyy')) : null,
        operator: 'eq',
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  useEffect(() => {
    if (props.value) {
      setTextBoxValue(moment(props.value).format('MM/DD/yyyy'));
    }
    if (inputRef.current?.element) {
      inputRef.current.element.setAttribute('inputmode', 'numeric');
      inputRef.current.element.setAttribute('aria-label', dateInputLabel);
      inputRef.current.element.setAttribute('title', dateInputLabel);
      inputRef.current.element.setAttribute('placeholder', 'MM/DD/YYYY');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, dateInputLabel]);

  return (
    <MaskedTextBox
      adaptive
      mask={'00/00/0000'}
      onChange={(event) => onTextboxChange(event)}
      value={textBoxValue}
      ref={inputRef}
      validityStyles={false}
      style={{ width: '100%' }}
      aria-label={dateInputLabel}
      title={dateInputLabel}
      placeholder="MM/DD/YYYY"
    />
  );
}

export function DateFilterCell(props) {
  const dateFilterLabel = props.ariaLabel || props.field || 'Date Filter';

  const onChange = (event) => {
    if (event.value === undefined) {
      return;
    }
    const momentObj = moment(event.value);

    // New version of DatePickers accept null as empty value only
    if (momentObj.isValid() || event.value === null) {
      props.onChange({
        value: momentObj.isValid() ? momentObj.format('MM/DD/yyyy') : '',
        operator: 'eq',
        syntheticEvent: event.syntheticEvent,
      });
    } else {
      props.onChange({
        value: null,
        target: {
          value: null,
        }
      });
    }
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData.getData('text');
    const parsedDate = moment(pastedValue, 'MM/DD/yyyy');

    if (parsedDate.isValid() && parsedDate >= moment('01/01/1900')) {
      props.onChange({
        value: pastedValue,
        operator: 'eq',
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  const onFocus = (event) => {
    event.target.addEventListener('paste', handlePaste);
  };

  const onBlur = (event) => {
    event.target.removeEventListener('paste', handlePaste);
  };

  return (
    <td>
      <div className={`k-filtercell ${styles.filterCellsInputContainer}`}>
        <DatePicker
          aria-label={dateFilterLabel}
          title={dateFilterLabel}
          format="MM/dd/yyyy"
          dateInput={FilterDatePickerMaskedBox}
          formatPlaceholder={{ year: 'yyyy', month: 'mm', day: 'dd' }}
          placeholder="mm/dd/yyyy"
          width={300}
          min={new Date(1900, 0, 1)}
          max={props.dateMax}
          value={props.value && props.value !== '' ? new Date(props.value) : null}
          autoFocus={false}
          onChange={(event) => onChange(event)}
          onFocus={(event) => onFocus(event)}
          onBlur={(event) => onBlur(event)}
        />
      </div>
    </td>
  );
}
